/* -----------------Helpers & Hooks--------------- */
import { useLocalSearchParams } from "expo-router";

import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useReviewCache } from "@features/feedback/hooks/useReviewCache";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useCacheKey from "@hooks/useCacheKey";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isWeb } from "@memorang/helpers/platform";
import { isReportSummativeTest } from "@memorang/helpers/report";
import { sortTagsBySortOrder } from "@memorang/helpers/tag";
/* -----------------Child components--------------- */
import { useMutationState, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getReport } from "../api";

export const useReport = () => {
	const params = useLocalSearchParams<{
		id: string;
	}>();

	const trackAppReviewCriteria = useReviewCache({
		isRoutedFromSession: !isWeb,
		shouldInvalidate: true,
	}).trackAppReviewCriteria;
	const id = params?.id;

	const { baseCacheKey } = useCacheKey();
	const updateAnswerEventInProgress = useSessionStore(
		(state) => state.updateAnswerEventInProgress,
	);

	const isPremiumUser = useGlobalStore((state) => state.isPremiumUser);

	const isSummativeTestFromSessionStore = useSessionStore(
		(state) => state.isSummativeTest,
	);
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const sessionId = useSessionStore((state) => state.id);

	const isAnswerEventInProgress = useSessionStore(
		(state) => state.isAnswerEventInProgress,
	);

	const { userPublicMetadataId: userId } = useAuthContext();

	const isEndingSessionInProgress = useSessionStore(
		(state) => state.isEndingSessionInProgress,
	);

	const pendingMutations = useMutationState({
		filters: {
			mutationKey: [baseCacheKey, cacheKeys.answerEvent],
			status: "pending",
		},
	});

	useEffect(() => {
		if (pendingMutations.length === 0) {
			updateAnswerEventInProgress(false);
		}
	}, [pendingMutations.length, updateAnswerEventInProgress]);

	const {
		data: reportDetails,
		isError: isReportError,
		isPending: isReportPending,
	} = useQuery({
		queryKey: ["report", id, userId],
		queryFn: async () => {
			// if (isMocked) {
			// 	return mockedReportResponse;
			// }
			const reportDetails = await getReport({
				sessionId: Number(id),
				userId: userId!,
			});
			const isSummativeTest = reportDetails
				? isReportSummativeTest(reportDetails, { studyPackCollectionId })
				: isSummativeTestFromSessionStore;
			if (
				reportDetails &&
				!isSummativeTest &&
				sessionId &&
				reportDetails.score &&
				isPremiumUser &&
				!isWeb
			) {
				const score =
					typeof reportDetails.score === "string"
						? Number.parseFloat(reportDetails.score)
						: reportDetails.score;
				trackAppReviewCriteria(score);
			}
			return {
				...reportDetails,
				tagDetails: sortTagsBySortOrder(reportDetails.tagDetails),
			};
		},
		enabled:
			!(isAnswerEventInProgress || isEndingSessionInProgress) &&
			Boolean(id) &&
			Boolean(userId),
	});

	return {
		reportDetails,
		isReportError,
		isReportPending,
	};
};
